import { useTranslation } from "react-i18next";
import TextInputField from "./TextInputField";
import TextAreaField from "./TextAreaField";
import { AtlasFieldRow, AtlasList, AtlasSection } from "./AtlasPrimitives";
import { AtlasKnowledge } from "../interfaces";

type AtlasTextKey = Exclude<keyof AtlasKnowledge, "objections">;

interface AtlasQuestion {
  key: AtlasTextKey;
  label: string;
  description: string;
  placeholder: string;
  rows?: number;
}

interface AtlasSectionConfig {
  id: string;
  title: string;
  description: string;
  questions: AtlasQuestion[];
}

// NOTE: labels/descriptions/placeholders are wrapped in t() at render time.
const ATLAS_SECTIONS: AtlasSectionConfig[] = [
  {
    id: "company",
    title: "Company",
    description: "What you sell and why it wins. Every product claim the agent makes comes from here.",
    questions: [
      {
        key: "companyWhat",
        label: "What do you sell, and who is it for?",
        description: "Plain language, like you'd tell a stranger. What it does, who it's for, roughly how it works.",
        placeholder: "e.g. HubSpot-native document automation — quotes, proposals and contracts generated straight from the deal, e-signed, and synced back. For sales teams that run on HubSpot…",
        rows: 5,
      },
      {
        key: "companyWhy",
        label: "Why do customers choose you over the alternatives?",
        description: "Your differentiators, and how you'd frame it against the other options — including doing nothing.",
        placeholder: "e.g. Other tools make you rebuild templates in their editor. We automate the Google Docs and Word templates teams already have. Live in a day, not a quarter…",
        rows: 5,
      },
      {
        key: "companyNot",
        label: "What are you NOT — and what should the agent never claim?",
        description: "The boundaries that keep it honest. Products you don't replace, features that aren't live, positions you never take.",
        placeholder: "e.g. Not a CRM, not a document editor. Never promise roadmap features. If a prospect needs something we don't do, say so…",
        rows: 4,
      },
    ],
  },
  {
    id: "customers",
    title: "Customers",
    description: "Who to sell to, who to walk away from, and who's in the room.",
    questions: [
      {
        key: "customersFit",
        label: "Who's a great fit — and who should be turned away?",
        description: "The profile of your best customers, and the deal-breakers where the honest answer is 'we're not right for you'.",
        placeholder: "e.g. Great fit: 20–250 person teams running deals in HubSpot with real document volume. Walk away: not on HubSpot, wants documents designed for them, just needs e-signatures…",
        rows: 5,
      },
      {
        key: "customersPeople",
        label: "Who's usually involved in a deal, and what does each person care about?",
        description: "The roles you meet — champion, budget holder, skeptic — and what to say to each.",
        placeholder: "e.g. RevOps manager feels the pain daily — talk time saved. The VP Sales signs — talk deal velocity and ROI. IT will ask about Drive access — lead with SOC 2…",
        rows: 5,
      },
      {
        key: "customersQualify",
        label: "What do you ask to qualify a deal quickly?",
        description: "The one or two questions that tell you the deal size and whether it's real.",
        placeholder: "e.g. 'How many reps create documents?' sets the tier. Then confirm deals actually live in HubSpot — if not, disqualify cleanly…",
        rows: 3,
      },
    ],
  },
  {
    id: "voice",
    title: "Voice",
    description: "How you sound. The agent layers this onto everything it drafts.",
    questions: [
      {
        key: "voiceSound",
        label: "How do you sound?",
        description: "A few adjectives or a sentence. The test every line has to pass.",
        placeholder: "e.g. Clear, confident, human. A smart friend, not a sales rep. Short sentences, British spelling, outcome before feature…",
        rows: 3,
      },
      {
        key: "voiceExamples",
        label: "Paste a few lines that sound exactly like you.",
        description: "Real sentences from your site or best emails — examples teach voice faster than rules.",
        placeholder: "e.g. 'Closed-won, not copy-paste.' / 'Your team rebuilds the same contract by hand for every client — Portant generates it straight from the deal.'",
        rows: 4,
      },
      {
        key: "voiceNever",
        label: "What would you never say?",
        description: "Banned words, openers, and habits — hype, false urgency, 'just checking in'.",
        placeholder: "e.g. No 'game-changing', 'revolutionary', 'supercharge'. Never open with 'just following up'. No emoji, no exclamation-mark spam, never disparage a competitor by name…",
        rows: 4,
      },
    ],
  },
  {
    id: "objections",
    title: "Objections",
    description: "The pushback you hear most, and how you answer it. Used as a lens, never pasted as a script.",
    questions: [],
  },
  {
    id: "pricing",
    title: "Pricing",
    description: "The hard limits on anything the agent says about price. It never improvises past these.",
    questions: [
      {
        key: "pricingStructure",
        label: "How does your pricing work, and what can the agent say about it?",
        description: "Plans, rough prices, and how to talk about them — including when to just point at the pricing page.",
        placeholder: "e.g. Free / Pro ~$42/mo / Team ~$125/mo. Agent can reference plans and published prices with 'around', never computes custom totals — point to the pricing page…",
        rows: 5,
      },
      {
        key: "pricingNever",
        label: "What must never be promised?",
        description: "Discounts, terms, dates, features — the commitments that always need a human.",
        placeholder: "e.g. No discounts unless already approved in the thread. No custom contract terms, no delivery dates, no price-matching, nothing legal or security beyond approved boilerplate…",
        rows: 4,
      },
    ],
  },
  {
    id: "playbook",
    title: "Playbook",
    description: "How your team actually runs a deal — so the agent's next-best-action matches your motion.",
    questions: [
      {
        key: "playbookDeal",
        label: "How does a typical deal run, first touch to close?",
        description: "The stages as you run them — what works at each step, the demo, the plays.",
        placeholder: "e.g. Open with an industry-keyed pain, never a generic intro. Qualify with the rep-count question. Demo in three chapters ending with e-sign. Recap after every call. Trade, never cave, on price…",
        rows: 6,
      },
      {
        key: "playbookEmails",
        label: "Which emails should the agent draft — and which should it leave to a human?",
        description: "The sends that convert vs the moments that need judgment or a call.",
        placeholder: "e.g. Draft: trial nudges, signature chasers, proposal follow-ups anchored to a date. Escalate: quiet deals with no hook — recommend a call or close-lost, not another check-in…",
        rows: 4,
      },
    ],
  },
];

function AtlasKnowledgeHub(props: { knowledge: AtlasKnowledge, onChange: (knowledge: AtlasKnowledge) => void }) {
  const { t } = useTranslation();
  const { knowledge } = props;

  function updateText(key: AtlasTextKey, value: string) {
    props.onChange({ ...knowledge, [key]: value });
  }

  return (
    <div className="flex flex-col pb-8 max-w-5xl">
      {ATLAS_SECTIONS.map((section, index) => (
        <AtlasSection key={section.id} id={section.id} index={index + 1} title={t(section.title)} description={t(section.description)}>
          {section.questions.map(question => (
            <AtlasFieldRow key={question.key} label={t(question.label)} description={t(question.description)}>
              <TextAreaField
                className="w-full text-sm"
                rows={question.rows ?? 4}
                value={knowledge[question.key]}
                onChange={v => updateText(question.key, v)}
                placeholder={t(question.placeholder)}
              />
            </AtlasFieldRow>
          ))}

          {section.id === "objections" &&
            <AtlasFieldRow
              label={t("The objections you hear most, and how you answer them")}
              description={t("Their words, then yours. Three to five covers most deals — add more as they recur.")}
            >
              <AtlasList
                items={knowledge.objections}
                onChange={items => props.onChange({ ...knowledge, objections: items })}
                blankItem={{ objection: "", response: "" }}
                addLabel={t("Add objection")}
                itemClassName="border border-gray-200 rounded-lg p-4"
                renderItem={(item, updateItem) => (
                  <div className="flex flex-col gap-2">
                    <TextInputField
                      className="w-full text-sm"
                      label={t("The objection")}
                      placeholder={t("e.g. \"It's more than we want to spend.\"")}
                      value={item.objection}
                      onChange={v => updateItem({ ...item, objection: v })}
                    />
                    <TextAreaField
                      className="w-full text-sm"
                      rows={2}
                      label={t("How you answer it")}
                      placeholder={t("e.g. Re-sell the outcome first, frame price against it — and never volunteer a discount.")}
                      value={item.response}
                      onChange={v => updateItem({ ...item, response: v })}
                    />
                  </div>
                )}
              />
            </AtlasFieldRow>
          }
        </AtlasSection>
      ))}
    </div>
  );
}

export default AtlasKnowledgeHub;
